import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  FormHelperText,
  CircularProgress,
  Alert,
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import { staffCreate } from "../../../Services/Services";
import { Person, Phone, Email, Lock, LocationOn } from "@mui/icons-material";
import { toast } from "react-toastify";
import { brandColor as brand } from "../../../theme";

const AddNewUser = ({ setUsers, setOpenAddModal }) => {
  const [formData, setFormData] = useState({
    name: "",
    mo_no: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: "",
  });

  const [errors, setErrors] = useState({
    mo_no: "",
    password: "",
    email: "",
  });
  const [responseMessage, setResponseMessage] = useState({
    type: "",
    text: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleMobileChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, [name]: numericValue }));
    if (errors.mo_no) {
      setErrors((prev) => ({ ...prev, mo_no: "" }));
    }
  };

  const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const validateInputs = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.mo_no.length !== 10) {
      newErrors.mo_no = "Mobile number must be exactly 10 digits";
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseMessage({ type: "", text: "" });

    const validationErrors = validateInputs();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        mo_no: formData.mo_no,
        password: formData.password,
        address: formData.address,
      };

      const res = await staffCreate(payload);
      setUsers(res);
      toast.success("User added successfully!");
      setOpenAddModal(false);

      setFormData({
        name: "",
        mo_no: "",
        email: "",
        password: "",
        confirmPassword: "",
        address: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add user.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      {responseMessage.text && (
        <Alert
          severity={responseMessage.type}
          sx={{ mb: 2, borderRadius: 2, mx: 2, mt: 2 }}
          onClose={() => setResponseMessage({ type: "", text: "" })}
        >
          {responseMessage.text}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2.5} sx={{ p: 2.5 }}>
          {/* Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Full Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              size="small"
              required
              error={!!errors.name}
              helperText={errors.name}
              InputProps={{
                startAdornment: (
                  <Person sx={{ fontSize: 18, color: brand, mr: 0.5 }} />
                ),
              }}
            />
          </Grid>

          {/* Mobile Number */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Mobile Number *"
              name="mo_no"
              value={formData.mo_no}
              onChange={handleMobileChange}
              size="small"
              required
              inputProps={{ maxLength: 10 }}
              error={!!errors.mo_no}
              helperText={errors.mo_no}
              InputProps={{
                startAdornment: (
                  <Phone sx={{ fontSize: 18, color: brand, mr: 0.5 }} />
                ),
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              value={formData.email}
              onChange={handleChange}
              size="small"
              type="email"
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <Email sx={{ fontSize: 18, color: brand, mr: 0.5 }} />
                ),
              }}
            />
          </Grid>

          {/* Password & Confirm Password */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Password *"
              name="password"
              value={formData.password}
              onChange={handleChange}
              size="small"
              type="password"
              required
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <Lock sx={{ fontSize: 18, color: brand, mr: 0.5 }} />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Confirm Password *"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              size="small"
              type="password"
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address *"
              name="address"
              value={formData.address}
              onChange={handleChange}
              size="small"
              multiline
              rows={2}
              required
              error={!!errors.address}
              helperText={errors.address}
              InputProps={{
                startAdornment: (
                  <LocationOn
                    sx={{
                      fontSize: 18,
                      color: brand,
                      mr: 0.5,
                      alignSelf: "flex-start",
                      mt: 1,
                    }}
                  />
                ),
              }}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => setOpenAddModal(false)}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 2,
                  py: 0.75,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": { borderColor: brand, color: brand },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  px: 2.5,
                  py: 0.75,
                  bgcolor: brand,
                  "&:hover": { bgcolor: "#4338ca" },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Add Staff"
                )}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddNewUser;
